import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api, { authService } from '../services/api';
import { getAvatarUrl } from './utils/Common';

const Settings = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    phone_number: ''
  });
  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState({ type: '', text: '' });
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Load departments
    loadDepartments();
    // Initialize form with user data
    if (user) {
      setFormData({
        phone_number: user.phone_number || ''
      });
    }
  }, [user]);

  const loadDepartments = async () => {
    try {
      const data = await authService.getDepartments();
      setDepartments(data);
    } catch (err) {
      console.error('Failed to load departments:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const updateData = {};
      
      // Only include changed fields
      if (formData.phone_number !== (user?.phone_number || '')) {
        updateData.phone_number = formData.phone_number || null;
      }

      // If nothing changed, show message
      if (Object.keys(updateData).length === 0) {
        setMessage({ type: 'info', text: 'No changes to save' });
        setSaving(false);
        return;
      }

      const success = await updateUser(updateData);
      if (success) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save settings' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    }

    setSaving(false);
  };

  const handleReset = () => {
    setFormData({
      phone_number: user?.phone_number || ''
    });
    setMessage({ type: '', text: '' });
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setAvatarMessage({ type: 'error', text: 'Invalid file type. Use JPEG, PNG, or WEBP.' });
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      setAvatarMessage({ type: 'error', text: 'File too large. Maximum size is 2 MB.' });
      return;
    }

    setAvatarUploading(true);
    setAvatarMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/auth/avatar/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update user context with new avatar
      await updateUser({ avatar: response.data.avatar });
      setAvatarMessage({ type: 'success', text: 'Avatar uploaded successfully!' });
    } catch (err) {
      console.error('Avatar upload error:', err);
      setAvatarMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to upload avatar' });
    } finally {
      setAvatarUploading(false);
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAvatar = async () => {
    if (!user?.avatar) return;

    setAvatarUploading(true);
    setAvatarMessage({ type: '', text: '' });

    try {
      await api.delete('/auth/avatar');
      await updateUser({ avatar: null });
      setAvatarMessage({ type: 'success', text: 'Avatar removed successfully!' });
    } catch (err) {
      console.error('Avatar delete error:', err);
      setAvatarMessage({ type: 'error', text: 'Failed to remove avatar' });
    } finally {
      setAvatarUploading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Ustawienia konta</h2>
        <p>Zarządzaj swoimi danymi osobowymi. Możesz dodać swój numer telefonu oraz zdjęcie profilowe.</p>
      </div>
      
      <div className="page-content">
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="settings-card">
          <form onSubmit={handleSubmit} className="settings-form">
            {/* Avatar Section */}
            <div className="form-section">
              <h3 className="form-section-title">Zdjęcie profilowe</h3>
              <p className="form-section-desc">Załącz zdjęcie profilowe (JPEG, PNG lub WEBP, maks. 2 MB)</p>
              
              <div className="avatar-section">
                <div className="avatar-preview" onClick={handleAvatarClick} style={{ cursor: 'pointer' }}>
                  {getAvatarUrl(user?.avatar) ? (
                    <img src={getAvatarUrl(user?.avatar)} alt="Profile" className="avatar-image" />
                  ) : (
                    <div className="avatar-placeholder">
                      <span className="avatar-initial">
                        {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  )}
                  {avatarUploading && (
                    <div className="avatar-loading">
                      <span>⏳</span>
                    </div>
                  )}
                </div>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarChange}
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                />
                
                <div className="avatar-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleAvatarClick}
                    disabled={avatarUploading}
                  >
                    Załącz zdjęcie
                  </button>
                  {user?.avatar && (
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={handleDeleteAvatar}
                      disabled={avatarUploading}
                    >
                      Usuń
                    </button>
                  )}
                </div>
                
                {avatarMessage.text && (
                  <div className={`avatar-message ${avatarMessage.type}`}>
                    {avatarMessage.text}
                  </div>
                )}
              </div>
            </div>

            {/* Read-only fields */}
            <div className="form-section">
              <h3 className="form-section-title">Informacje o koncie</h3>
              <p className="form-section-desc">Te pola nie mogą być zmieniane</p>
              
              <div className="form-group">
                <label>Imię i nazwisko</label>
                <input
                  type="text"
                  value={user?.full_name || ''}
                  disabled
                  className="form-input disabled"
                />
              </div>

              <div className="form-group">
                <label>Adres E-mail</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="form-input disabled"
                />
              </div>

              <div className="form-group">
                <label>Jednostka organizacyjna</label>
                <select
                  value={user?.department || ''}
                  disabled
                  className="form-input form-select disabled"
                >
                  {departments.map(dept => (
                    <option key={dept} value={dept}>
                      {dept.charAt(0).toUpperCase() + dept.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Stanowisko</label>
                <input
                  type="text"
                  value={user?.position_name || '-'}
                  disabled
                  className="form-input disabled"
                />
              </div>
            </div>

            {/* Editable fields */}
            <div className="form-section">
              <h3 className="form-section-title">Dane osobowe</h3>
              <p className="form-section-desc">Aktualizuj swoje dane osobowe</p>

              <div className="form-group">
                <label htmlFor="phone_number">Numer telefonu</label>
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  placeholder="Wprowadź swój numer telefonu"
                  className="form-input"
                />
              </div>
            </div>

            {/* Account Status (read-only) */}
            <div className="form-section">
              <h3 className="form-section-title">Status konta</h3>
              
              <div className="status-info">
                <div className="status-item">
                  <span className="status-label">Status:</span>
                  <span className={`status-badge ${user?.is_active ? 'active' : 'inactive'}`}>
                    {user?.is_active ? 'Aktywny' : 'Nieaktywny'}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">Członek od:</span>
                  <span>{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                {saving ? 'Zapisywanie...' : 'Zapisz zmiany'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleReset}
                disabled={saving}
              >
                Resetuj
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;
